import { addDocument, getDocuments, updateDocument, deleteDocument } from './firebaseService';
import { BlogPost, BlogFormData } from '../types/BlogPost';

// Generate URL-friendly slug from title
export const generateSlug = (title: string): string => {
  try {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  } catch (error) {
    console.error('Error generating slug:', error);
    return 'blog-post';
  }
};

// Calculate estimated reading time (average 200 words per minute)
export const calculateReadingTime = (content: string): number => {
  try {
    const wordsPerMinute = 200;
    const wordCount = content.trim().split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  } catch (error) {
    console.error('Error calculating reading time:', error);
    return 1;
  }
};

// Create a new blog post
export const createBlogPost = async (formData: BlogFormData): Promise<string | null> => {
  try {
    const slug = generateSlug(formData.title);
    const readingTime = calculateReadingTime(formData.content);
    const now = new Date().toISOString();

    const blogPost: Omit<BlogPost, 'id'> = {
      title: formData.title,
      slug,
      description: formData.description,
      content: formData.content,
      imageUrl: formData.imageUrl,
      imageAlt: formData.imageAlt,
      author: formData.author,
      publishedAt: now,
      updatedAt: now,
      status: 'published',
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      metaKeywords: formData.metaKeywords,
      readingTime,
      featured: formData.featured
    };

    return await addDocument('blogPosts', blogPost);
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

// Get all blog posts
export const getBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const documents = await getDocuments('blogPosts');
    return documents as BlogPost[];
  } catch (error) {
    console.error('Error getting blog posts:', error);
    throw error;
  }
};

// Get published blog posts only
export const getPublishedBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const allPosts = await getBlogPosts();
    return allPosts
      .filter(post => post.status === 'published')
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  } catch (error) {
    console.error('Error getting published blog posts:', error);
    throw error;
  }
};

// Get featured blog posts
export const getFeaturedBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const publishedPosts = await getPublishedBlogPosts();
    return publishedPosts.filter(post => post.featured).slice(0, 3);
  } catch (error) {
    console.error('Error getting featured blog posts:', error);
    throw error;
  }
};

// Get blog post by slug
export const getBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  try {
    const allPosts = await getBlogPosts();
    return allPosts.find(post => post.slug === slug) || null;
  } catch (error) {
    console.error('Error getting blog post by slug:', error);
    throw error;
  }
};

// Update blog post
export const updateBlogPost = async (id: string, formData: BlogFormData): Promise<void> => {
  try {
    const slug = generateSlug(formData.title);
    const readingTime = calculateReadingTime(formData.content);

    const updateData = {
      title: formData.title,
      slug,
      description: formData.description,
      content: formData.content,
      imageUrl: formData.imageUrl,
      imageAlt: formData.imageAlt,
      author: formData.author,
      updatedAt: new Date().toISOString(),
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      metaKeywords: formData.metaKeywords,
      readingTime,
      featured: formData.featured
    };

    await updateDocument('blogPosts', id, updateData);
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

// Delete blog post
export const deleteBlogPost = async (id: string): Promise<void> => {
  try {
    await deleteDocument('blogPosts', id);
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
}; 